import React, { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

/** Commercial amCharts 5 key removes the corner logo when valid (see https://www.amcharts.com/docs/v5/getting-started/commercial-license/). */
const AMCHARTS5_LICENSE = process.env.REACT_APP_AMCHARTS5_LICENSE;
if (typeof AMCHARTS5_LICENSE === "string" && AMCHARTS5_LICENSE.length > 0) {
  am5.addLicense(AMCHARTS5_LICENSE);
}

/** Sub slice: category, value (for slice size), optional leadCount, percentage, color, status_type and valueForApi for click-to-expand. */
export interface ExplodingPieSubItem {
  category: string;
  value: number;
  leadCount?: number;
  percentage?: number;
  color?: string;
  /** status_type for API (e.g. wip, closed). */
  status_type?: string;
  /** Value sent to click-to-expand API (e.g. funnel_stage name); defaults to category if not set. */
  valueForApi?: string;
}

/** Main series data item with embedded subData for drill-down. */
export interface ExplodingPieDataItem {
  category: string;
  value: number;
  /** Actual lead count to show in label/tooltip (e.g. 0); when set, overrides value for display. */
  leadCount?: number;
  /** Optional percentage (e.g. 16.67) for label display. */
  percentage?: number;
  subData: ExplodingPieSubItem[];
  /** Optional hex color for main slice (e.g. "#10b981"). */
  color?: string;
  /** Optional status_type for click-to-expand API (e.g. active, wip, closed, reopen). */
  status_type?: string;
}

/** Params passed when a main-pie or sub-pie slice is clicked (for click-to-expand modal). */
export interface ExplodingPieSliceClickParams {
  category: string;
  status_type?: string;
  value: string;
}

export type ExplodingPieSubSliceClickParams = ExplodingPieSliceClickParams;

const DEFAULT_MAIN_DATA: ExplodingPieDataItem[] = [
  {
    category: "Lithuania",
    value: 500,
    subData: [
      { category: "A", value: 200 },
      { category: "B", value: 150 },
      { category: "C", value: 100 },
      { category: "D", value: 100 },
    ],
  },
  {
    category: "Czechia",
    value: 300,
    subData: [{ category: "A", value: 150 }],
  },
  {
    category: "Ireland",
    value: 200,
    subData: [
      { category: "A", value: 110 },
      { category: "B", value: 60 },
      { category: "C", value: 30 },
    ],
  },
  {
    category: "Germany",
    value: 150,
    subData: [
      { category: "A", value: 80 },
      { category: "B", value: 40 },
      { category: "C", value: 30 },
    ],
  },
  {
    category: "Australia",
    value: 140,
    subData: [
      { category: "A", value: 90 },
      { category: "B", value: 40 },
      { category: "C", value: 10 },
    ],
  },
  {
    category: "Austria",
    value: 120,
    subData: [
      { category: "A", value: 60 },
      { category: "B", value: 30 },
      { category: "C", value: 30 },
    ],
  },
];

/** Minimum value to show a slice (avoids tiny slivers and label overlap when most counts are zero). */
const MIN_SLICE_VALUE = 0.5;

/** Placeholder sub series entries (hidden when not used). */
const SUB_PLACEHOLDERS = [
  { category: "A", value: 0 },
  { category: "B", value: 0 },
  { category: "C", value: 0 },
  { category: "D", value: 0 },
  { category: "E", value: 0 },
  { category: "F", value: 0 },
  { category: "G", value: 0 },
];

export interface ExplodingPieChartProps {
  mainData?: ExplodingPieDataItem[];
  height?: number;
  className?: string;
  /** Called when a main-pie slice is clicked. */
  onSliceClick?: (params: ExplodingPieSliceClickParams) => void;
  /** Called when a sub-pie (right) slice is clicked – use this for click-to-expand modal. */
  onSubSliceClick?: (params: ExplodingPieSubSliceClickParams) => void;
}

const ExplodingPieChart: React.FC<ExplodingPieChartProps> = ({
  mainData = DEFAULT_MAIN_DATA,
  height = 500,
  className = "",
  onSliceClick,
  onSubSliceClick,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    // Hide default “created with amCharts” corner mark (also suppressed when addLicense above is valid).
    const logo = (root as unknown as { _logo?: am5.Container })._logo;
    logo?.dispose();

    // Only show slices with meaningful value (avoids tiny slivers and overlapping labels when most counts are zero)
    const filteredMainData = mainData.filter((d) => Number(d.value) >= MIN_SLICE_VALUE);
    const dataToUse = filteredMainData.length > 0 ? filteredMainData : mainData;

    const container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.horizontalLayout,
      })
    );

    // Main pie chart
    const chart = container.children.push(
      am5percent.PieChart.new(root, {
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        fillField: "color",
        alignLabels: false,
      })
    );
    const hasColors = dataToUse.some((d) => d.color != null);
    const colorSet = am5.ColorSet.new(root, {});
    const subColorSet = am5.ColorSet.new(root, { step: 2 });
    const toAm5Color = (c: string | am5.Color | undefined): am5.Color | undefined => {
      if (c == null) return undefined;
      if (typeof c === "string") return (am5.Color as any).fromString(c);
      return c as am5.Color;
    };
    const mainDataWithColor = dataToUse.map((d) => ({
      ...d,
      color: d.color != null ? toAm5Color(d.color) : colorSet.next(),
    }));

    series.labels.template.setAll({
      textType: "circular",
      radius: 4,
    });
    const displayCount = (ctx: any) => (ctx.leadCount != null ? Number(ctx.leadCount) : ctx.value != null ? Number(ctx.value) : null);
    series.labels.template.adapters.add("text", (text, target: any) => {
      const dataItem = target.dataItem;
      const ctx = dataItem?.dataContext;
      if (!ctx) return text;
      const count = displayCount(ctx);
      if (count !== null) return `${ctx.category}\n${count} leads`;
      return ctx.category ?? text;
    });
    series.slices.template.adapters.add("tooltipText", (text, target: any) => {
      const dataItem = target.dataItem;
      const ctx = dataItem?.dataContext;
      if (!ctx) return text;
      const count = displayCount(ctx);
      if (count !== null) return `${ctx.category}\n${count} leads`;
      return ctx.category ?? String(text);
    });
    series.ticks.template.set("visible", false);
    series.slices.template.set("toggleKey", "none");
    series.slices.template.set("stroke", am5.color(0xffffff));
    series.slices.template.set("strokeWidth", 1);

    let selectedSlice: am5.Slice | undefined;

    series.slices.template.events.on("click", (ev) => {
      const slice = ev.target;
      if (slice && slice.dataItem) selectSlice(slice as am5.Slice);
    });

    // Sub pie chart (smaller, right side)
    const subChart = container.children.push(
      am5percent.PieChart.new(root, {
        radius: am5.percent(50),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const subSeries = subChart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        fillField: "color",
      })
    );

    subSeries.data.setAll(SUB_PLACEHOLDERS);
    subSeries.slices.template.set("toggleKey", "none");
    subSeries.slices.template.set("stroke", am5.color(0xffffff));
    subSeries.slices.template.set("strokeWidth", 1);
    subSeries.labels.template.setAll({
      textType: "circular",
      radius: 4,
    });
    subSeries.labels.template.adapters.add("text", (text, target: any) => {
      const dataItem = target.dataItem;
      const ctx = dataItem?.dataContext;
      if (!ctx) return text;
      const count = displayCount(ctx);
      if (count !== null) return `${ctx.category}\n${count} leads`;
      return ctx.category ?? text;
    });
    subSeries.slices.template.adapters.add("tooltipText", (text, target: any) => {
      const dataItem = target.dataItem;
      const ctx = dataItem?.dataContext;
      if (!ctx) return text;
      const count = displayCount(ctx);
      if (count !== null) return `${ctx.category}\n${count} leads`;
      return ctx.category ?? String(text);
    });
    subSeries.ticks.template.set("visible", false);
    subSeries.slices.template.events.on("click", (ev) => {
      const slice = ev.target;
      const dataItem = slice?.dataItem;
      const ctx = dataItem?.dataContext as (ExplodingPieSubItem & { valueForApi?: string }) | undefined;
      if (onSubSliceClick && ctx) {
        onSubSliceClick({
          category: ctx.category,
          status_type: ctx.status_type,
          value: ctx.valueForApi ?? ctx.category,
        });
      }
    });

    // Connecting lines (dashed)
    const line0 = container.children.push(
      am5.Line.new(root, {
        position: "absolute",
        stroke: root.interfaceColors.get("text"),
        strokeDasharray: [2, 2],
      })
    );
    const line1 = container.children.push(
      am5.Line.new(root, {
        position: "absolute",
        stroke: root.interfaceColors.get("text"),
        strokeDasharray: [2, 2],
      })
    );

    function updateLines() {
      if (!selectedSlice) return;
      // When only one slice, hide connecting lines (full circle makes lines look like a V from one point)
      if (dataToUse.length <= 1) {
        line0.set("visible", false);
        line1.set("visible", false);
        return;
      }
      line0.set("visible", true);
      line1.set("visible", true);

      const startAngle = (selectedSlice as any).get("startAngle");
      const arc = (selectedSlice as any).get("arc");
      const radius = (selectedSlice as any).get("radius");
      if (startAngle == null || arc == null || radius == null) return;

      const x00 = radius * am5.math.cos(startAngle);
      const y00 = radius * am5.math.sin(startAngle);
      const x10 = radius * am5.math.cos(startAngle + arc);
      const y10 = radius * am5.math.sin(startAngle + arc);

      const subSlice0 = subSeries.slices.getIndex(0);
      const subRadius = subSlice0 ? (subSlice0 as any).get("radius") : 50;
      const x01 = 0;
      const y01 = -subRadius;
      const x11 = 0;
      const y11 = subRadius;

      const point00 = series.toGlobal({ x: x00, y: y00 });
      const point10 = series.toGlobal({ x: x10, y: y10 });
      const point01 = subSeries.toGlobal({ x: x01, y: y01 });
      const point11 = subSeries.toGlobal({ x: x11, y: y11 });

      line0.set("points", [point00, point01]);
      line1.set("points", [point10, point11]);
    }

    const toSubRow = (d: ExplodingPieSubItem) => ({
      category: d.category,
      value: d.value,
      leadCount: d.leadCount,
      percentage: d.percentage,
      status_type: d.status_type,
      valueForApi: d.valueForApi,
      color: toAm5Color(d.color) ?? subColorSet.next(),
    });

    function selectSlice(slice: am5.Slice) {
      selectedSlice = slice;
      const dataItem = slice.dataItem;
      const dataContext = dataItem?.dataContext as ExplodingPieDataItem | undefined;

      const validSubData = (dataContext?.subData ?? []).filter((d) => Number(d.value) >= MIN_SLICE_VALUE);
      if (validSubData.length > 0) {
        subSeries.data.setAll(validSubData.map(toSubRow));
      } else {
        // No subData (e.g. Active, Reopen): show one full pie on the right with same category, value, color, percentage
        const sliceColor = dataContext?.color != null ? toAm5Color(dataContext.color as string | am5.Color) : subColorSet.next();
        subSeries.data.setAll([
          {
            category: dataContext?.category ?? "Total",
            value: dataContext?.value ?? 0,
            leadCount: dataContext?.leadCount,
            percentage: dataContext?.percentage,
            color: sliceColor,
            status_type: dataContext?.status_type,
            valueForApi: dataContext?.category,
          },
        ]);
      }

      const middleAngle = (slice as any).get("startAngle") + (slice as any).get("arc") / 2;
      const firstDataItem = series.dataItems[0];
      const firstSlice = firstDataItem?.get("slice");
      const firstAngle = firstSlice ? (firstSlice as any).get("startAngle") : 0;

      series.animate({
        key: "startAngle",
        to: firstAngle - middleAngle,
        duration: 1000,
        easing: am5.ease.out(am5.ease.cubic),
      });
      series.animate({
        key: "endAngle",
        to: firstAngle - middleAngle + 360,
        duration: 1000,
        easing: am5.ease.out(am5.ease.cubic),
      });
    }

    series.on("startAngle", () => {
      updateLines();
    });

    container.events.on("boundschanged", () => {
      root.events.once("frameended", () => {
        updateLines();
      });
    });

    series.data.setAll(hasColors ? mainDataWithColor : dataToUse.map((d) => ({ ...d, color: colorSet.next() })));
    container.appear(1000, 10);

    series.events.on("datavalidated", () => {
      const first = series.slices.getIndex(0);
      if (first) selectSlice(first as am5.Slice);
    });

    return () => {
      root.dispose();
    };
  }, [mainData]);

  return (
    <div
      ref={chartRef}
      className={className}
      style={{
        width: "100%",
        height: `${height}px`,
        minHeight: 0,
      }}
    />
  );
};

export default ExplodingPieChart;
